import { useCallback, useReducer, useRef, useEffect, useState } from 'react'
import { Moment } from 'moment'
import {
  GOSearchMenusParams,
  SearchGOMenusResponse,
  GroupedMenus,
} from '@coordinators/groupOrders/menus'
import { Cuisine, Option, GOScheduleMenu } from '@types'

// Types for our hook
export interface UseGOMenuSearchProps {
  searchGOMenus: (
    params: GOSearchMenusParams,
  ) => Promise<SearchGOMenusResponse | null>
  loadCuisines: () => Promise<Cuisine[]>
  displayFailureMessage: (mssg: string) => void
  hqId: number
  clientId?: string
  clientName?: string
  latitude?: number
  longitude?: number
  clientSettingsId?: string
  withinBudget?: boolean
}

export interface UseGOMenuSearchResult {
  menus: GOScheduleMenu[]
  groupedMenus: GroupedMenus | null
  cuisineOptions: Option[]
  chefOptions: Option[]
  menuOptions: Option[]
  availOn: Moment | null
  filters: Filters
  sortBy: [keyof GOScheduleMenu, boolean, (a: any) => any]
  totalMenus: number
  loadingMenus: boolean
  readyToSearch: boolean
  setAvailOn: (availOn: Moment | null) => void
  setCuisines: (cuisines: string[] | null) => void
  setExcludeClientSpecificMenus: (excludeClientSpecificMenus: boolean) => void
  setMenucardCutoffs: (menucardCutoffs: number[] | null) => void
  setExcludeChefIds: (excludeChefIds: string[] | null) => void
  setExcludeMenuIds: (excludeMenuIds: string[] | null) => void
  setReadyToHeat: (readyToHeat: boolean | null) => void
  setAvgMenuPriceGT: (avgMenuPriceGT: number | null) => void
  setAvgMenuPriceLT: (avgMenuPriceLT: number | null) => void
  setAvgMenuMarginGT: (avgMenuMarginGT: number | null) => void
  setAvgMenuMarginLT: (avgMenuMarginLT: number | null) => void
  setVendorMaxItemsGT: (vendorMaxItemsGT: number | null) => void
  setVendorMaxItemsLT: (vendorMaxItemsLT: number | null) => void
  setMenuOrderingCapGT: (menuOrderingCapGT: number | null) => void
  setMenuOrderingCapLT: (menuOrderingCapLT: number | null) => void
  setAvgRating: (avgRating: number | null) => void
  setDistanceLT: (distanceLT: number | null) => void
  setLastScheduledGT: (lastScheduledGT: number | null) => void
  setLastScheduledLT: (lastScheduledLT: number | null) => void
  setSortBy: (sortBy: [keyof GOScheduleMenu, boolean, (a: any) => any]) => void
  executeSearchGOMenus: () => Promise<GOScheduleMenu[]>
  resetSearch: () => void
  sortMenus: (
    attribute: keyof GOScheduleMenu,
    transform?: (a: any) => any,
  ) => void
  sortByIcon: (attribute: string) => string
  onUpdateMenuInState: (menu: GOScheduleMenu) => void
  setAllFilters: (filters: Filters) => void
  setMenus: (menus: GOScheduleMenu[]) => void
  excludeMenuAndUpdate: (menuId: string) => void
}

// Action types for our reducer
type MenuAction =
  | { type: 'RESET_SEARCH' }
  | { type: 'SET_AVAIL_ON'; payload: Moment | null }
  | { type: 'SET_CUISINES'; payload: string[] | null }
  | { type: 'SET_MENUCARD_CUTOFFS'; payload: number[] | null }
  | { type: 'SET_EXCLUDE_CHEF_IDS'; payload: string[] | null }
  | { type: 'SET_EXCLUDE_MENU_IDS'; payload: string[] | null }
  | { type: 'SET_READY_TO_HEAT'; payload: boolean | null }
  | { type: 'SET_AVG_MENU_PRICE_GT'; payload: number | null }
  | { type: 'SET_AVG_MENU_PRICE_LT'; payload: number | null }
  | { type: 'SET_AVG_MENU_MARGIN_GT'; payload: number | null }
  | { type: 'SET_AVG_MENU_MARGIN_LT'; payload: number | null }
  | { type: 'SET_VENDOR_MAX_ITEMS_GT'; payload: number | null }
  | { type: 'SET_VENDOR_MAX_ITEMS_LT'; payload: number | null }
  | { type: 'SET_MENU_ORDERING_CAP_GT'; payload: number | null }
  | { type: 'SET_MENU_ORDERING_CAP_LT'; payload: number | null }
  | { type: 'SET_AVG_RATING'; payload: number | null }
  | { type: 'SET_DISTANCE_LT'; payload: number | null }
  | { type: 'SET_LAST_SCHEDULED_GT'; payload: number | null }
  | { type: 'SET_LAST_SCHEDULED_LT'; payload: number | null }
  | { type: 'EXCLUDE_MENU_AND_UPDATE'; payload: string }
  | {
      type: 'SET_SORT_BY'
      payload: [keyof GOScheduleMenu, boolean, (a: any) => any]
    }
  | { type: 'SET_MENUS'; payload: GOScheduleMenu[] }
  | { type: 'SET_GROUPED_MENUS'; payload: GroupedMenus | null }
  | { type: 'SET_LOADING_MENUS'; payload: boolean }
  | { type: 'SET_ALL_FILTERS'; payload: Filters }
  | { type: 'SET_EXCLUDE_CLIENT_SPECIFIC_MENUS'; payload: boolean }

// Interface for filters
interface Filters {
  cuisines: string[] | null
  menucardCutoffs: number[] | null
  excludeChefIds: string[] | null
  excludeMenuIds: string[] | null
  readyToHeat: boolean | null
  avgMenuPriceGT: number | null
  avgMenuPriceLT: number | null
  avgMenuMarginGT: number | null
  avgMenuMarginLT: number | null
  vendorMaxItemsGT: number | null
  vendorMaxItemsLT: number | null
  menuOrderingCapGT: number | null
  menuOrderingCapLT: number | null
  avgRating: number | null
  distanceLT: number | null
  lastScheduledGT: number | null
  lastScheduledLT: number | null
  excludeClientSpecificMenus: boolean
}

// Export the Filters interface so it can be imported elsewhere
export type { Filters }

// State interface for our reducer
interface MenuState {
  menus: GOScheduleMenu[]
  groupedMenus: GroupedMenus | null
  availOn: Moment | null
  sortBy: [
    attribute: keyof GOScheduleMenu,
    asc: boolean,
    transform: (a: any) => any,
  ]
  loadingMenus: boolean
  filters: Filters
}

// Initial state for our reducer
const initialState: MenuState = {
  menus: [],
  groupedMenus: null,
  availOn: null,
  sortBy: ['name', true, (name) => name.toLowerCase()],
  loadingMenus: false,
  filters: {
    cuisines: null,
    menucardCutoffs: null,
    excludeChefIds: null,
    excludeMenuIds: null,
    readyToHeat: null,
    avgMenuPriceGT: null,
    avgMenuPriceLT: null,
    avgMenuMarginGT: null,
    avgMenuMarginLT: null,
    vendorMaxItemsGT: null,
    vendorMaxItemsLT: null,
    menuOrderingCapGT: null,
    menuOrderingCapLT: null,
    avgRating: null,
    distanceLT: null,
    lastScheduledGT: null,
    lastScheduledLT: null,
    excludeClientSpecificMenus: true,
  },
}

const frontEndFilterMenusHelper = (
  menus: GOScheduleMenu[],
  state: MenuState,
): GOScheduleMenu[] => {
  const { menucardCutoffs, excludeChefIds, excludeMenuIds } = state.filters

  const filteredMenus = menus.filter((menu) => {
    // Filter by menucard cutoff days
    const menucardCutoffsMatch =
      !menucardCutoffs || menucardCutoffs.includes(menu.groupOrderCutoffDays)

    // Filter out menus from excluded chefs
    const excludeChefIdsMatch =
      !excludeChefIds || !excludeChefIds.includes(menu.chef.id)

    // Filter out specific excluded menus
    const excludeMenuIdsMatch =
      !excludeMenuIds || !excludeMenuIds.includes(menu.id)

    return menucardCutoffsMatch && excludeChefIdsMatch && excludeMenuIdsMatch
  })

  return filteredMenus
}

// Helper function to sort menus
const sortMenusHelper = (
  menus: GOScheduleMenu[],
  sortBy: [keyof GOScheduleMenu, boolean, (a: any) => any],
) => {
  const [attribute, asc, transform] = sortBy
  const actualTransform = transform || ((a: any) => a)

  return [...menus].sort((a, b) => {
    const valueA = a[attribute]
    const valueB = b[attribute]

    // Handle case where one value might be undefined or null
    if (valueA === undefined || valueA === null) {
      return asc ? -1 : 1
    }
    if (valueB === undefined || valueB === null) {
      return asc ? 1 : -1
    }

    const transformedA = actualTransform(valueA)
    const transformedB = actualTransform(valueB)

    if (typeof transformedA === 'string' && typeof transformedB === 'string') {
      return asc
        ? transformedA.localeCompare(transformedB)
        : transformedB.localeCompare(transformedA)
    }

    return asc ? transformedA - transformedB : transformedB - transformedA
  })
}

// Our reducer function
const menuReducer = (state: MenuState, action: MenuAction): MenuState => {
  switch (action.type) {
    case 'RESET_SEARCH':
      return {
        ...initialState,
      }
    case 'SET_AVAIL_ON':
      return {
        ...state,
        availOn: action.payload,
      }
    case 'SET_CUISINES':
      return {
        ...state,
        filters: {
          ...state.filters,
          cuisines: action.payload,
        },
      }
    case 'SET_MENUCARD_CUTOFFS':
      return {
        ...state,
        filters: {
          ...state.filters,
          menucardCutoffs: action.payload,
        },
      }
    case 'SET_EXCLUDE_CHEF_IDS':
      return {
        ...state,
        filters: {
          ...state.filters,
          excludeChefIds: action.payload,
        },
      }
    case 'SET_EXCLUDE_MENU_IDS':
      return {
        ...state,
        filters: {
          ...state.filters,
          excludeMenuIds: action.payload,
        },
      }
    case 'SET_READY_TO_HEAT':
      return {
        ...state,
        filters: {
          ...state.filters,
          readyToHeat: action.payload,
        },
      }
    case 'SET_AVG_MENU_PRICE_GT':
      return {
        ...state,
        filters: {
          ...state.filters,
          avgMenuPriceGT: action.payload,
        },
      }
    case 'SET_AVG_MENU_PRICE_LT':
      return {
        ...state,
        filters: {
          ...state.filters,
          avgMenuPriceLT: action.payload,
        },
      }
    case 'SET_AVG_MENU_MARGIN_GT':
      return {
        ...state,
        filters: {
          ...state.filters,
          avgMenuMarginGT: action.payload,
        },
      }
    case 'SET_AVG_MENU_MARGIN_LT':
      return {
        ...state,
        filters: {
          ...state.filters,
          avgMenuMarginLT: action.payload,
        },
      }
    case 'SET_VENDOR_MAX_ITEMS_GT':
      return {
        ...state,
        filters: {
          ...state.filters,
          vendorMaxItemsGT: action.payload,
        },
      }
    case 'SET_VENDOR_MAX_ITEMS_LT':
      return {
        ...state,
        filters: {
          ...state.filters,
          vendorMaxItemsLT: action.payload,
        },
      }
    case 'SET_MENU_ORDERING_CAP_GT':
      return {
        ...state,
        filters: {
          ...state.filters,
          menuOrderingCapGT: action.payload,
        },
      }
    case 'SET_MENU_ORDERING_CAP_LT':
      return {
        ...state,
        filters: {
          ...state.filters,
          menuOrderingCapLT: action.payload,
        },
      }
    case 'SET_AVG_RATING':
      return {
        ...state,
        filters: {
          ...state.filters,
          avgRating: action.payload,
        },
      }
    case 'SET_DISTANCE_LT':
      return {
        ...state,
        filters: {
          ...state.filters,
          distanceLT: action.payload,
        },
      }
    case 'SET_LAST_SCHEDULED_GT':
      return {
        ...state,
        filters: {
          ...state.filters,
          lastScheduledGT: action.payload,
        },
      }
    case 'SET_LAST_SCHEDULED_LT':
      return {
        ...state,
        filters: {
          ...state.filters,
          lastScheduledLT: action.payload,
        },
      }
    case 'SET_SORT_BY':
      return {
        ...state,
        sortBy: action.payload,
      }
    case 'SET_MENUS': {
      const filteredMenus = frontEndFilterMenusHelper(action.payload, state)
      const menus = sortMenusHelper(filteredMenus, state.sortBy)

      return {
        ...state,
        menus,
      }
    }
    case 'SET_GROUPED_MENUS': {
      return {
        ...state,
        groupedMenus: action.payload,
      }
    }
    case 'SET_LOADING_MENUS': {
      return {
        ...state,
        loadingMenus: action.payload,
      }
    }
    case 'EXCLUDE_MENU_AND_UPDATE': {
      // Get the menu ID to exclude
      const menuIdToExclude = action.payload

      // Create updated excludeMenuIds array
      const currentExcludedMenuIds = state.filters.excludeMenuIds || []
      const updatedExcludeMenuIds = [...currentExcludedMenuIds, menuIdToExclude]

      // Filter out the menu from the menus array
      const updatedMenus = state.menus.filter(
        (menu) => menu.id !== menuIdToExclude,
      )

      // Return updated state with new excludeMenuIds filter and updated menus
      return {
        ...state,
        menus: updatedMenus,
        filters: {
          ...state.filters,
          excludeMenuIds: updatedExcludeMenuIds,
        },
      }
    }
    case 'SET_ALL_FILTERS':
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload,
        },
      }
    case 'SET_EXCLUDE_CLIENT_SPECIFIC_MENUS':
      return {
        ...state,
        filters: {
          ...state.filters,
          excludeClientSpecificMenus: action.payload,
        },
      }
    default:
      return state
  }
}

export const useGOMenuSearch = ({
  searchGOMenus,
  loadCuisines,
  displayFailureMessage,
  hqId,
  clientId,
  clientName,
  latitude,
  longitude,
  clientSettingsId,
  withinBudget,
}: UseGOMenuSearchProps): UseGOMenuSearchResult => {
  // Use reducer for state management
  const [state, dispatch] = useReducer(menuReducer, initialState)
  const [cuisineOptions, setCuisineOptions] = useState<Option[]>([])
  const [chefOptions, setChefOptions] = useState<Option[]>([])
  const [menuOptions, setMenuOptions] = useState<Option[]>([])

  // Destructure state for easier access
  const { menus, groupedMenus, availOn, sortBy, loadingMenus, filters } = state

  // Track if the client ID, HQ ID, or location changes
  const previousClientIdRef = useRef<string | undefined>(undefined)
  const previousHqIdRef = useRef<number | undefined>(undefined)
  const previousLocationRef = useRef<
    { lat?: number; lng?: number } | undefined
  >(undefined)

  // Load cuisines on mount
  const onLoadCuisines = useCallback(async () => {
    const cuisineTags = await loadCuisines()
    const cuisines = cuisineTags.map((c: Cuisine) => {
      return {
        label: c.name,
        value: c.name,
      }
    })
    setCuisineOptions(cuisines)
  }, [loadCuisines])

  useEffect(() => {
    onLoadCuisines()
  }, [onLoadCuisines])

  // Function to build GOSearchMenusParams from current state
  const buildSearchParams = useCallback((): GOSearchMenusParams => {
    return {
      hqId,
      latitude,
      longitude,
      clientId,
      clientName,
      clientSettingsId,
      availOn,
      withinBudget,
      avgMenuPriceGT: state.filters.avgMenuPriceGT,
      avgMenuPriceLT: state.filters.avgMenuPriceLT,
      avgMenuMarginGT: state.filters.avgMenuMarginGT,
      avgMenuMarginLT: state.filters.avgMenuMarginLT,
      cuisines: state.filters.cuisines,
      readyToHeat: state.filters.readyToHeat,
      vendorMaxItemsGT: state.filters.vendorMaxItemsGT,
      vendorMaxItemsLT: state.filters.vendorMaxItemsLT,
      menuOrderingCapGT: state.filters.menuOrderingCapGT,
      menuOrderingCapLT: state.filters.menuOrderingCapLT,
      avgRating: state.filters.avgRating,
      distanceLT: state.filters.distanceLT,
      lastScheduledGT: state.filters.lastScheduledGT,
      lastScheduledLT: state.filters.lastScheduledLT,
      excludeClientSpecificMenus: state.filters.excludeClientSpecificMenus,
    }
  }, [
    hqId,
    latitude,
    longitude,
    clientId,
    clientName,
    clientSettingsId,
    withinBudget,
    availOn,
    state.filters,
  ])

  // Check if required filter params are set
  const readyToSearch = Boolean(hqId)

  // Execute menu search
  const executeSearchGOMenus = useCallback(async (): Promise<
    GOScheduleMenu[]
  > => {
    if (!readyToSearch || loadingMenus) {
      return []
    }

    dispatch({ type: 'SET_LOADING_MENUS', payload: true })
    let foundMenus: GOScheduleMenu[] = []

    try {
      const params = buildSearchParams()
      const searchResponse = await searchGOMenus(params)
      if (!searchResponse) {
        return []
      }

      foundMenus = searchResponse.finalMenus
      dispatch({
        type: 'SET_GROUPED_MENUS',
        payload: searchResponse.groupedMenus,
      })

      const nextMenuOptions = foundMenus
        .map((menu) => ({
          value: menu.id,
          label: `${menu.name} (${menu.chef.name})`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
      setMenuOptions(nextMenuOptions)

      // Update chef options from loaded menus
      const uniqueChefs = new Map<string, { id: string; name: string }>()
      foundMenus.forEach((menu) => {
        if (menu.chef && menu.chef.id) {
          uniqueChefs.set(menu.chef.id, menu.chef)
        }
      })
      // Add to existing chef options
      chefOptions.forEach((chefOption) => {
        if (uniqueChefs.has(chefOption.value)) {
          uniqueChefs.set(chefOption.value, {
            id: chefOption.value,
            name: chefOption.label,
          })
        }
      })

      // Convert to options format
      const chefOptionsList = Array.from(uniqueChefs.values()).map((chef) => ({
        value: chef.id,
        label: chef.name,
      }))

      // Sort by chef name
      chefOptionsList.sort((a, b) => a.label.localeCompare(b.label))
      setChefOptions(chefOptionsList)

      dispatch({ type: 'SET_MENUS', payload: foundMenus })
    } catch (error) {
      displayFailureMessage(`Error fetching GO menus: ${error}`)
    } finally {
      dispatch({ type: 'SET_LOADING_MENUS', payload: false })
    }

    return foundMenus
  }, [
    buildSearchParams,
    searchGOMenus,
    setMenuOptions,
    readyToSearch,
    displayFailureMessage,
    chefOptions,
  ])

  // Reset search
  const resetSearch = useCallback(() => {
    dispatch({ type: 'RESET_SEARCH' })
  }, [])

  // Sort menus
  const sortMenus = useCallback(
    (attribute: keyof GOScheduleMenu, transform?: (a: any) => any) => {
      const asc = attribute !== sortBy[0] || !sortBy[1]
      const actualTransform = transform || ((a: any) => a)
      const nextSortBy: [keyof GOScheduleMenu, boolean, (a: any) => any] = [
        attribute,
        asc,
        actualTransform,
      ]
      dispatch({ type: 'SET_SORT_BY', payload: nextSortBy })

      // Re-sort the menus with the new sort criteria
      const sortedMenus = sortMenusHelper(state.menus, nextSortBy)
      dispatch({ type: 'SET_MENUS', payload: sortedMenus })
    },
    [sortBy, state.menus],
  )

  // Get sort icon
  const sortByIcon = useCallback(
    (attribute: string) => {
      const [sortAttribute, isAsc] = sortBy
      if (sortAttribute === attribute) {
        return isAsc ? '▲' : '▼'
      }

      return ''
    },
    [sortBy],
  )

  // Effects to trigger search when important parameters change
  useEffect(() => {
    const clientIdChanged = clientId !== previousClientIdRef.current
    const hqIdChanged = hqId !== previousHqIdRef.current
    const locationChanged =
      latitude !== previousLocationRef.current?.lat ||
      longitude !== previousLocationRef.current?.lng

    if (clientIdChanged || hqIdChanged || locationChanged) {
      if (clientIdChanged || hqIdChanged || locationChanged) {
        resetSearch()
      }

      previousClientIdRef.current = clientId
      previousHqIdRef.current = hqId
      previousLocationRef.current = { lat: latitude, lng: longitude }
    }
  }, [clientId, hqId, latitude, longitude, resetSearch])

  // Create setters that dispatch actions
  const setAvailOn = useCallback((availOn: Moment | null) => {
    dispatch({ type: 'SET_AVAIL_ON', payload: availOn })
  }, [])

  const setCuisines = useCallback((cuisines: string[] | null) => {
    dispatch({ type: 'SET_CUISINES', payload: cuisines })
  }, [])

  const setMenucardCutoffs = useCallback((menucardCutoffs: number[] | null) => {
    dispatch({ type: 'SET_MENUCARD_CUTOFFS', payload: menucardCutoffs })
  }, [])

  const setExcludeChefIds = useCallback((excludeChefIds: string[] | null) => {
    dispatch({ type: 'SET_EXCLUDE_CHEF_IDS', payload: excludeChefIds })
  }, [])

  const setExcludeMenuIds = useCallback((excludeMenuIds: string[] | null) => {
    dispatch({ type: 'SET_EXCLUDE_MENU_IDS', payload: excludeMenuIds })
  }, [])

  const setReadyToHeat = useCallback((readyToHeat: boolean | null) => {
    dispatch({ type: 'SET_READY_TO_HEAT', payload: readyToHeat })
  }, [])

  const setAvgMenuPriceGT = useCallback((avgMenuPriceGT: number | null) => {
    dispatch({ type: 'SET_AVG_MENU_PRICE_GT', payload: avgMenuPriceGT })
  }, [])

  const setAvgMenuPriceLT = useCallback((avgMenuPriceLT: number | null) => {
    dispatch({ type: 'SET_AVG_MENU_PRICE_LT', payload: avgMenuPriceLT })
  }, [])

  const setAvgMenuMarginGT = useCallback((avgMenuMarginGT: number | null) => {
    dispatch({ type: 'SET_AVG_MENU_MARGIN_GT', payload: avgMenuMarginGT })
  }, [])

  const setAvgMenuMarginLT = useCallback((avgMenuMarginLT: number | null) => {
    dispatch({ type: 'SET_AVG_MENU_MARGIN_LT', payload: avgMenuMarginLT })
  }, [])

  const setVendorMaxItemsGT = useCallback((vendorMaxItemsGT: number | null) => {
    dispatch({ type: 'SET_VENDOR_MAX_ITEMS_GT', payload: vendorMaxItemsGT })
  }, [])

  const setVendorMaxItemsLT = useCallback((vendorMaxItemsLT: number | null) => {
    dispatch({ type: 'SET_VENDOR_MAX_ITEMS_LT', payload: vendorMaxItemsLT })
  }, [])

  const setMenuOrderingCapGT = useCallback(
    (menuOrderingCapGT: number | null) => {
      dispatch({ type: 'SET_MENU_ORDERING_CAP_GT', payload: menuOrderingCapGT })
    },
    [],
  )

  const setMenuOrderingCapLT = useCallback(
    (menuOrderingCapLT: number | null) => {
      dispatch({ type: 'SET_MENU_ORDERING_CAP_LT', payload: menuOrderingCapLT })
    },
    [],
  )

  const setAvgRating = useCallback((avgRating: number | null) => {
    dispatch({ type: 'SET_AVG_RATING', payload: avgRating })
  }, [])

  const setDistanceLT = useCallback((distanceLT: number | null) => {
    dispatch({ type: 'SET_DISTANCE_LT', payload: distanceLT })
  }, [])

  const setLastScheduledGT = useCallback((lastScheduledGT: number | null) => {
    dispatch({ type: 'SET_LAST_SCHEDULED_GT', payload: lastScheduledGT })
  }, [])

  const setLastScheduledLT = useCallback((lastScheduledLT: number | null) => {
    dispatch({ type: 'SET_LAST_SCHEDULED_LT', payload: lastScheduledLT })
  }, [])

  const setSortBy = useCallback(
    (sortBy: [keyof GOScheduleMenu, boolean, (a: any) => any]) => {
      dispatch({ type: 'SET_SORT_BY', payload: sortBy })
    },
    [],
  )

  const onUpdateMenuInState = useCallback(
    (menu: GOScheduleMenu) => {
      // Find and update the menu in the current state
      const updatedMenus = state.menus.map((m) => (m.id === menu.id ? menu : m))
      dispatch({ type: 'SET_MENUS', payload: updatedMenus })
    },
    [state.menus],
  )

  // New function to set all filters at once
  const setAllFilters = useCallback((filters: Filters) => {
    dispatch({ type: 'SET_ALL_FILTERS', payload: filters })
  }, [])

  const setMenus = useCallback((menus: GOScheduleMenu[]) => {
    dispatch({ type: 'SET_MENUS', payload: menus })
  }, [])

  // New function to exclude a menu and update state
  const excludeMenuAndUpdate = useCallback((menuId: string) => {
    dispatch({ type: 'EXCLUDE_MENU_AND_UPDATE', payload: menuId })
  }, [])

  const setExcludeClientSpecificMenus = useCallback(
    (excludeClientSpecificMenus: boolean) => {
      dispatch({
        type: 'SET_EXCLUDE_CLIENT_SPECIFIC_MENUS',
        payload: excludeClientSpecificMenus,
      })
    },
    [],
  )

  return {
    menus,
    groupedMenus,
    cuisineOptions,
    chefOptions,
    menuOptions,
    availOn,
    filters,
    sortBy,
    totalMenus: menus.length,
    loadingMenus,
    readyToSearch,
    setAvailOn,
    setCuisines,
    setMenucardCutoffs,
    setExcludeChefIds,
    setExcludeMenuIds,
    setReadyToHeat,
    setAvgMenuPriceGT,
    setAvgMenuPriceLT,
    setAvgMenuMarginGT,
    setAvgMenuMarginLT,
    setVendorMaxItemsGT,
    setVendorMaxItemsLT,
    setMenuOrderingCapGT,
    setMenuOrderingCapLT,
    setAvgRating,
    setDistanceLT,
    setLastScheduledGT,
    setLastScheduledLT,
    setSortBy,
    setMenus,
    executeSearchGOMenus,
    resetSearch,
    sortMenus,
    sortByIcon,
    onUpdateMenuInState,
    setAllFilters,
    excludeMenuAndUpdate,
    setExcludeClientSpecificMenus,
  }
}
